import { Grid } from "@mui/material";
import { useEvents } from "api/events";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { SelectInput } from "components/FormControls/Select/Select";
import { TextInput } from "components/FormControls/TextInput";
import { useMessage } from "hooks/intl";
import { useManageInfiniteScroll } from "hooks/useManageInfiniteScroll";
import _ from "lodash";
import { memo, useMemo, useState } from "react";
import {
  CodeGeneratorCharacterSetType,
  SelectedSchemaCondition,
} from "types/campaigns";
import { alphabetizeByProperty } from "utilities/arrays";
import { getInitialDataForSelectedSchemaConditions } from "../../../../Common/CampaignRules/rules";

interface Props {
  isEditMode: boolean;
  event?: string;
}

export const RedemptionCode = memo(({ isEditMode, event }: Props) => {
  const [customEventsSearch, setSearchEvent] = useState<string>();
  const { message } = useMessage();
  const { useInfiniteCustomEventsSchemaList, useCustomEventSchemaDetails } =
    useEvents();
  const { data: selectedSchemaDetails } = useCustomEventSchemaDetails(event);

  const {
    data: infiniteCustomEventSchemaList,
    size: customEventSchemaSize,
    setSize: setCustomEventSchemaSize,
    isValidating,
  } = useInfiniteCustomEventsSchemaList({
    filters: customEventsSearch
      ? [
          {
            condition: "like",
            query: "name",
            value: customEventsSearch,
          },
          {
            condition: "eq",
            query: "active",
            value: true,
          },
        ]
      : [
          {
            condition: "eq",
            query: "active",
            value: true,
          },
        ],
    order: "asc",
    rows: 50,
    sortKey: "name",
  });

  const {
    infiniteData: customEventSchemaList,
    anyElementsToFetch: anyCustomEventSchemaToFetch,
  } = useManageInfiniteScroll(infiniteCustomEventSchemaList || []);

  const isSchemaAllowed = ({
    schema,
  }: {
    schema: { fields: { type: string }[] };
  }) => {
    const allowedFieldTypes = ["boolean", "datetime", "number", "text"];
    return schema?.fields?.every(({ type }: { type: string }) =>
      allowedFieldTypes.includes(type),
    );
  };
  const allowedSchemas = customEventSchemaList.filter(isSchemaAllowed) || [];

  const selectedSchemaConditions: SelectedSchemaCondition[] =
    selectedSchemaDetails?.schema?.fields.map(
      ({
        name,
        type,
        description,
      }: {
        name: string;
        type: string;
        description: string;
      }) => {
        return {
          attribute: `event.body.${name}`,
          data: getInitialDataForSelectedSchemaConditions(type),
          label: description || name,
          name: name,
          operator: type === "boolean" ? "is_equal" : undefined,
          type: type,
        };
      },
    ) || [];

  const characterSets = useMemo(
    () =>
      alphabetizeByProperty(
        [
          {
            id: CodeGeneratorCharacterSetType.ALPHA,
            label: message({ id: "alpha" }),
          },
          {
            id: CodeGeneratorCharacterSetType.ALPHANUM,
            label: message({ id: "alphanum" }),
          },
          {
            id: CodeGeneratorCharacterSetType.NUM,
            label: message({ id: "num" }),
          },
        ],
        "label",
      ),
    [message],
  );
  const initialEventValue = selectedSchemaDetails && {
    id: selectedSchemaDetails.eventType as string,
    label: selectedSchemaDetails.name,
  };

  return (
    <Grid container mt={0} spacing={12}>
      <Grid item xs={4}>
        <SelectInput
          displaySearch
          data-test-id="campaign-trigger-redemption-code-search"
          width="100%"
          items={
            allowedSchemas.map((event) => ({
              label: event.name,
              id: event.eventType,
            })) || []
          }
          label={
            <RequiredFieldLabel
              text={message({
                id: "eventName",
              })}
            />
          }
          name="event"
          asyncSearch={(searchedQuery: string) => setSearchEvent(searchedQuery)}
          initialValue={initialEventValue}
          placeholder={message({ id: "eventName" })}
          anyElementsToFetch={anyCustomEventSchemaToFetch}
          loadMoreElements={() =>
            !isValidating && setCustomEventSchemaSize(customEventSchemaSize + 1)
          }
          disabled={isEditMode}
        />
      </Grid>
      <Grid item xs={4}>
        <SelectInput
          disabled={isEditMode || _.isEmpty(selectedSchemaConditions)}
          items={alphabetizeByProperty(
            selectedSchemaConditions?.map((condition) => ({
              id: condition.name,
              label: condition.label,
            })),
            "label",
          )}
          label={
            <RequiredFieldLabel
              text={message({
                id: "attributeEventName",
              })}
            />
          }
          name="eventCodeAttribute"
          data-test-id="campaign-trigger-redemption-code-attribute"
        />
      </Grid>
      <Grid xs={12} md={4} item>
        {!isEditMode && (
          <TextInput
            label={
              <RequiredFieldLabel
                text={message({
                  id: "numberOfGeneratedCodes",
                })}
              />
            }
            name="generateCodes"
            type="number"
            data-test-id="campaign-trigger-redemption-code-number-of-codes-input"
          />
        )}
      </Grid>
      <Grid xs={12} md={4} item>
        <TextInput
          label={
            <RequiredFieldLabel
              text={message({
                id: "redemptionCodeLength",
              })}
            />
          }
          name="codeGenerator.length"
          type="number"
          data-test-id="campaign-trigger-redemption-code-length-input"
        />
      </Grid>
      <Grid xs={12} md={4} item>
        <TextInput
          label={message({
            id: "redemptionCodePrefix",
          })}
          name="codeGenerator.prefix"
          data-test-id="campaign-trigger-redemption-code-prefix-input"
        />
      </Grid>
      <Grid xs={12} md={4} item>
        <SelectInput
          items={characterSets}
          label={
            <RequiredFieldLabel
              text={message({
                id: "redemptionCodeCharacterSet",
              })}
            />
          }
          name="codeGenerator.characterSet"
          data-test-id="campaign-trigger-redemption-code-char-set-select"
        />
      </Grid>
    </Grid>
  );
});
